import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationAutomationPersonnelAlgorithmsDefinitionModal from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/components/modal/configuration-automation-personnel-algorithms-definition-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import {
    ConfigurationAutomationPersonnelAlgorithmsDefinitionGridState,
    initialConfigurationAutomationPersonnelAlgorithmsDefinitionGridState
} from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/components/grid/configuration-automation-personnel-algorithms-definition-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import {
    ConfigurationAutomaticAssignmentAlgorithmCreateRequest,
    ConfigurationAutomaticAssignmentAlgorithmPatchInBatchRequest,
    ConfigurationAutomaticAssignmentAlgorithmResponse
} from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/services/configuration-automatic-assignment-algorithm/configuration-automatic-assignment-algorithm.dto'

import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationAutomationPersonnelAlgorithmsDefinitionModal
    }
})
export default class ConfigurationAutomationPersonnelAlgorithmsDefinitionGrid extends BaseCrudServerSideComponent<ConfigurationAutomaticAssignmentAlgorithmResponse> implements BaseCrud {
    @Ref() readonly algorithmsDefinitionModalWrapper!: typeof ModalWrapper

    state: ConfigurationAutomationPersonnelAlgorithmsDefinitionGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationAutomationPersonnelAlgorithmsDefinitionGridState()
    }

    /// SERVICES CALLS ///

    private async _createAutomaticAssignmentAlgorithm(createRequest: ConfigurationAutomaticAssignmentAlgorithmCreateRequest): Promise<ConfigurationAutomaticAssignmentAlgorithmResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchAutomaticAssignmentAlgorithms(batchRequests: ConfigurationAutomaticAssignmentAlgorithmPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteAutomaticAssignmentAlgorithms(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async createOperation(createRequest: ConfigurationAutomaticAssignmentAlgorithmCreateRequest): Promise<void> {
        if (createRequest) {
            const automaticAssignmentAlgorithm = await this._createAutomaticAssignmentAlgorithm(createRequest)
            if (automaticAssignmentAlgorithm) {
                this.reloadData()
                this.onUpdate(automaticAssignmentAlgorithm)
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async patchOperation(batchRequest: ConfigurationAutomaticAssignmentAlgorithmPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchAutomaticAssignmentAlgorithms([batchRequest])) {
                this.reloadData()
            }
        }
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteAutomaticAssignmentAlgorithms(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(automaticAssignmentAlgorithm: ConfigurationAutomaticAssignmentAlgorithmResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (automaticAssignmentAlgorithm) {
            this.showModal(this.$tc('configuration.actions.update'), automaticAssignmentAlgorithm, this.algorithmsDefinitionModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(automaticAssignmentAlgorithm => automaticAssignmentAlgorithm.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.algorithmsDefinitionModalWrapper)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }
}
